import { CalendarOrClockPickerView } from '@mui/x-date-pickers/internals/models';

export const FIELD_TYPES = {
	string: 'string',
	select: 'select',
	date: 'date',
	number: 'number',
	currency: 'currency',
	email: 'email',
	lookup: 'lookup',
	group: 'group',
	media: 'media',
	policyLookup: 'policyLookup',
	policyholderLookup: 'policyholderLookup',
};

export const FORM_MESSAGE = {
	number: 'Must be a number',
	postiveNum: 'Must be a positive value',
	negativeNum: 'Must be a negative value',
	required: 'Required',
	notImplemented: 'Not yet implemented',
	emptySelectValue: 'None',
	invalidDate: 'Invalid Date',
	noFields: 'No configured fields',
	thirtyTwoCharacterLimit: 'Must be 32 characters or less',
	noSpecialCharacters: 'Special characters are not allowed',
	noSpecialCharactersOrWhitespace:
		'Special characters and whitespace are not allowed',
};

export const GROUP_FIELD = {
	delete: 'Delete',
	add: 'Add New',
};

export const MEDIA_FIELD = {
	UPLOAD: 'Upload',
	NO_PERMISSION: 'Media hidden due to insufficient permissions',
	ARIA_LABEL: {
		UPLOAD: 'upload',
		DOWNLOAD: 'download',
		REMOVE: 'remove',
	},
};

export const PERIL_LIMIT_SUBMITTED_FIELDS = {
	indemnityInAggregate: 'indemnityInAggregate',
	indemnityPerEvent: 'indemnityPerEvent',
	indemnityPerItem: 'indemnityPerItem',
	lumpSumPayment: 'lumpSumPayment',
	deductible: 'deductible',
};

export const PERIL_LIMIT_REMOVE_FIELD_TO_LIMIT_FIELD_MAP = {
	removeDeductible: 'deductible',
	removeIndemnityInAggregate: 'indemnityInAggregate',
	removeIndemnityPerEvent: 'indemnityPerEvent',
	removeIndemnityPerItem: 'indemnityPerItem',
	removeLumpSumPayment: 'lumpSumPayment',
};

export const PERIL_FIELD_TITLES = {
	indemnityPerItemType: 'Indemnity Per Item Type',
	indemnityPerItem: 'Indemnity Per Item',
	indemnityPerEventType: 'Indemnity Per Event Type',
	indemnityPerEvent: 'Indemnity Per Event',
	indemnityPerAggregateType: 'Indemnity Per Aggregate Type',
	indemnityPerAggregate: 'Indemnity Per Aggregate',
	lumpSumPaymentType: 'Lump Sum Payment Type',
	lumpSumPayment: 'Lump Sum Payment',
	deductibleType: 'Deductible Type',
	deductible: 'Deductible',
};

export const PERIL_FIELD_NAMES = {
	indemnityPerItemType: 'indemnityPerItemType',
	indemnityPerItem: 'indemnityPerItem',
	indemnityPerEventType: 'indemnityPerEventType',
	indemnityPerEvent: 'indemnityPerEvent',
	indemnityPerAggregateType: 'indemnityPerAggregateType',
	indemnityPerAggregate: 'indemnityPerAggregate',
	lumpSumPaymentType: 'lumpSumPaymentType',
	lumpSumPayment: 'lumpSumPayment',
	deductibleType: 'deductibleType',
	deductible: 'deductible',
};

export const PERIL_FIELD_UPDATE_VALUES = {
	unlimited: 'unlimited',
	limited: 'limited',
	set: 'set',
	unset: 'unset',
};

export const PERIL_FIELD_VALUES = {
	unlimited: 'Unlimited',
	limited: 'Limited',
	set: 'Set',
	unset: 'Unset',
};

export const FAST_FORM_SHAPE_FIELD_KEYS = [
	'values',
	'fieldGroupsByLocator',
	'fields',
	'mediaByLocator',
	'externalDataCalls',
	'addFieldGroups',
	'removeFieldGroup',
	'fieldGroups',
];

export const DATE_PRECISION = {
	YEAR: 'year',
	MONTH: 'month',
	DAY: 'day',
	HOUR: 'hour',
	MINUTE: 'minute',
	SECOND: 'second',
};

export const DATE_PRECISION_BACKEND_FORMAT = {
	[DATE_PRECISION.YEAR]: 'YYYY',
	[DATE_PRECISION.MONTH]: 'YYYY-MM',
	[DATE_PRECISION.DAY]: 'YYYY-MM-DD',
	[DATE_PRECISION.HOUR]: 'YYYY-MM-DDTHH:mm:ssZZ',
	[DATE_PRECISION.MINUTE]: 'YYYY-MM-DDTHH:mm:ssZZ',
	[DATE_PRECISION.SECOND]: 'YYYY-MM-DDTHH:mm:ssZZ',
};

export const DATE_PRECISION_INPUT_FORMAT = {
	[DATE_PRECISION.YEAR]: 'YYYY',
	[DATE_PRECISION.MONTH]: 'YYYY/MM',
	[DATE_PRECISION.DAY]: 'YYYY/MM/DD',
	[DATE_PRECISION.HOUR]: 'YYYY/MM/DD hh:00 a',
	[DATE_PRECISION.MINUTE]: 'YYYY/MM/DD hh:mm a',
	[DATE_PRECISION.SECOND]: 'YYYY/MM/DD hh:mm:ss a',
};

export const DATE_PRECISION_LUXON_FORMAT = {
	[DATE_PRECISION.YEAR]: 'yyyy',
	[DATE_PRECISION.MONTH]: 'yyyy/MM',
	[DATE_PRECISION.DAY]: 'yyyy/MM/dd',
	[DATE_PRECISION.HOUR]: 'yyyy/MM/dd hh:00 a',
	[DATE_PRECISION.MINUTE]: 'yyyy/MM/dd hh:mm a',
	[DATE_PRECISION.SECOND]: 'yyyy/MM/dd hh:mm:ss a',
};

export const DATE_PRECISION_VIEWS: {
	[views: string]: CalendarOrClockPickerView[];
} = {
	[DATE_PRECISION.YEAR]: ['year'],
	[DATE_PRECISION.MONTH]: ['year', 'month'],
	[DATE_PRECISION.DAY]: ['year', 'month', 'day'],
	[DATE_PRECISION.HOUR]: ['year', 'month', 'day', 'hours'],
	[DATE_PRECISION.MINUTE]: ['year', 'month', 'day', 'hours', 'minutes'],
	[DATE_PRECISION.SECOND]: [
		'year',
		'month',
		'day',
		'hours',
		'minutes',
		'seconds',
	],
};

export const DATE_PRECISION_PLACEHOLDER = {
	[DATE_PRECISION.YEAR]: 'yyyy',
	[DATE_PRECISION.MONTH]: 'yyyy/mm',
	[DATE_PRECISION.DAY]: 'yyyy/mm/dd',
	[DATE_PRECISION.HOUR]: 'yyyy/mm/dd hh:00 (am|pm)',
	[DATE_PRECISION.MINUTE]: 'yyyy/mm/dd hh:mm (am|pm)',
	[DATE_PRECISION.SECOND]: 'yyyy/mm/dd hh:mm:ss (am|pm)',
};

export const NUMBER_FIELD_VALIDATIONS = {
	MUST_BE_A_DIGIT_REGEX: new RegExp(/^(-?\d*\.?\d+)?$/),
	MUST_BE_A_DIGIT_MESSAGE: 'Must be a digit',
	MIN_VALUE_ERROR_MESSAGE: 'Must be greater than or equal to ',
	MAX_VALUE_ERROR_MESSAGE: 'Must be less than or equal to ',
	DECIMAL_PLACES_ERROR_MESSAGE: 'Must have ',
	DECIMAL_PLACES_ERROR_MESSAGE_2: ' or fewer decimal places',
};

export const STRING_FIELD_VALIDATIONS = {
	REGEX_ERROR_MESSAGE: 'Must match regex: ',
};

export const DATE_FIELD_VALIDATIONS = {
	INVALID_DATE_ERROR_MESSAGE: 'Invalid date',
	INVALID_FORMAT_ERROR_MESSAGE: 'Format must be: ',
	MIN_DATE_ERROR_MESSAGE: 'Date must be on or after ',
	MAX_DATE_ERROR_MESSAGE: 'Date must be on or before ',
};

export const FORM_NAME = {
	ENDORSEMENT_FORM: 'endorsementForm',
	EXPOSURE_FORM: 'exposureForm',
	RENEWAL_FORM: 'renewalForm',
	PREMIUM_REPORT_FORM: 'premiumReportForm',
	POLICY_FORM: 'policyForm',
	policyholder: 'policyholder',
	policy: 'policy',
	claim: 'claim',
	payment: 'payment',
	disbursement: 'disbursement',
	balanceTransfer: 'balanceTransfer',
};

export const POLICYHOLDER_FIELD_TYPE = {
	string: 'type.string',
	number: 'type.number',
	day: 'date.precision.day',
	date: 'type.date',
	select: 'type.datasource.string',
	email: 'type.email',
};

export const CLAIM_SPECIAL_FIELDS = {
	INCIDENT_DATE: 'incidentDate',
	NOTIFICATION_DATE: 'notificationDate',
};

export const PAYMENT_SPECIAL_FIELDS = {
	amountCurrency: 'amountCurrency',
	amount: 'amount',
	policyLocator: 'policyLocator',
	policyholderLocator: 'policyholderLocator',
};

export const DISBURSEMENT_SPECIAL_FIELDS = {
	amountCurrency: 'amountCurrency',
	amount: 'amount',
	policyLocator: 'policyLocator',
	policyholderLocator: 'policyholderLocator',
};

export const BALANCE_TRANSFER_SPECIAL_FIELDS = {
	policyholderTo: 'policyholderTo',
	policyTo: 'policyTo',
	policyholderFrom: 'policyholderFrom',
	policyFrom: 'policyFrom',
	name: 'name',
	amount: 'amount',
	details: 'details',
};
